import React, { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Provider } from 'mobx-react'
import { ErrorBoundary } from 'react-error-boundary'
import { MantineProvider } from '@mantine/core'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import { UserManager } from 'oidc-client-ts'
import GlobalStore from './Stores/GlobalStore'
import { Routes } from './Pages/Routes'
import { DeviceHeartbeat } from './Components/DeviceHeartbeat'
import { AuthWrapper, authConfig } from './Auth/AuthWrapper'
import { UpdateMonitor } from './Components/UpdateMonitor'
import { initDatadog, setDeviceContext, trackError } from './Utils/datadog'
import '@mantine/core/styles.css'
import './styles.scss'
import './app.scss'

const isEmbedded = (() => {
  try {
    return window.top !== window.self
  } catch {
    return true
  }
})()

if (!isEmbedded) {
  initDatadog()
}

const appInsights = new ApplicationInsights({
  config: {
    connectionString: import.meta.env.VITE_APP_INSIGHTS_CONNECTION_STRING as string,
    disableExceptionTracking: false,
  },
})

appInsights.loadAppInsights()

appInsights.addTelemetryInitializer(telemetryItem => {
  const config = GlobalStore.configFetch.data
  if (telemetryItem.data) {
    telemetryItem.data['appName'] = 'KDS'
    telemetryItem.data['siteId'] = config?.siteId
    telemetryItem.data['departmentId'] = config?.departmentId
    telemetryItem.data['departmentName'] = config?.departmentName
  }
})

window.appInsights = appInsights

// SPA runs in two modes: standalone and admin
const getRouterBasename = () => {
  const path = window.location.pathname
  return path === '/kdsapp' || path.startsWith('/kdsapp/') ? '/kdsapp' : '/'
}

const addMSW = async () => {
  if (import.meta.env.VITE_ENABLE_MOCK_SERVER) {
    const { worker } = await import('./mocks/browser')
    return worker.start()
  }
  return Promise.resolve()
}

const loadConfig = async () => {
  await addMSW()
  await GlobalStore.fetchConfig()

  const config = GlobalStore.configFetch.data
  if (config) {
    setDeviceContext({
      siteUid: config.siteId,
      departmentUid: config.departmentId,
      departmentName: config.departmentName,
    })
  }
}

const Fallback = ({ resetErrorBoundary }: { resetErrorBoundary: () => void }) => {
  return (
    <div role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>Something went wrong</h2>
      <button onClick={resetErrorBoundary}>Try Again</button>
    </div>
  )
}

const App = () => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    loadConfig()
      .then(() => {
        setIsLoaded(true)
      })
      .catch(err => {
        trackError(err, { source: 'config.load' })
        setError(err)
      })
  }, [])

  const attemptReconnect = () => {
    setError(null)
    setIsLoaded(false)
    loadConfig()
      .then(() => {
        setIsLoaded(true)
      })
      .catch(err => {
        trackError(err, { source: 'config.reload' })
        setTimeout(() => {
          setError(err)
        }, 300)
      })
  }

  if (error) {
    return (
      <div className="app__error">
        <UpdateMonitor />
        <div className="app__error__text">
          <h2>Something Went Wrong</h2>
          <p>There was an error loading the Kitchen Display System.</p>
          <button className="button" onClick={attemptReconnect}>
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div className="app__loading">
        <UpdateMonitor />
        <div className="fs-loading-large"></div>
      </div>
    )
  }

  return (
    <ErrorBoundary
      FallbackComponent={Fallback}
      onError={error => {
        window.appInsights?.trackException({
          exception: error,
        })
        trackError(error, { source: 'react.error_boundary' })
      }}
    >
      <MantineProvider>
        <Provider GlobalStore={GlobalStore}>
          <UpdateMonitor />
          <BrowserRouter basename={getRouterBasename()}>
            <Routes />
          </BrowserRouter>
        </Provider>
      </MantineProvider>
    </ErrorBoundary>
  )
}

// The prompt=none renew iframe lands here. Complete the handshake so
// oidc-client-ts posts the result to the parent window, then render nothing —
// this document is throwaway. Must run before the isEmbedded branch below,
// which would otherwise boot a second KDS inside the iframe and never finish
// the silent renew.
if (window.location.pathname.endsWith('/silent-callback')) {
  new UserManager(authConfig).signinSilentCallback().catch(err => {
    trackError(err, { source: 'auth.silent_callback' })
  })
} else {
  createRoot(document.getElementById('root')!).render(
    isEmbedded ? (
      <App />
    ) : (
      <AuthWrapper>
        <DeviceHeartbeat />
        <App />
      </AuthWrapper>
    )
  )
}
